import { SnapDenyCodes } from '../types';
import { createSnapError } from './sanitize';
import type { SnapError } from '../types';

/**
 * PR-8.7C customer-safe error catalogue for embedded Snap.
 * Never surfaces raw RFC7807, stacks, AppKey, GUIDs as primary text, or Panel login prompts.
 */
export const CustomerMessages = {
  applicationSignInRequired: 'Please sign in to this application to use Snap Support.',
  applicationSessionExpired: 'Your application session has expired. Sign in again to continue.',
  snapNotEnabled: 'Snap Support is not enabled for this application.',
  snapNotInPlan: 'Snap Support is not included in this plan.',
  userNotAuthorized: 'You are not authorized to use Snap Support.',
  sessionExpired: 'Your Snap Support session has expired. Refresh the page and try again.',
  temporarilyUnavailable: 'Support is temporarily unavailable. Please try again later.',
  categoryLoadFailed: 'Categories could not be loaded. You can still submit without a category.',
  captureUnavailable: 'Screenshot capture is unavailable in this browser.',
  attachmentRejected: 'That attachment could not be accepted. Check the file type and size.',
  submissionFailed: 'Your request could not be submitted. Please try again.',
  requestSubmitted: 'Your support request has been submitted.',
  myRequestsUnavailable: 'My Requests is unavailable right now.',
  createDenied: 'You cannot create a support request with your current access.',
  bootstrapUnauthorized: 'Snap Support could not authorize this session.'
} as const;

export function mapCustomerError(input: {
  code?: string | null;
  message?: string | null;
  httpStatus?: number | null;
  correlationId?: string | null;
}): SnapError {
  const code = String(input.code || '').trim();
  const raw = String(input.message || '').trim();
  const status = input.httpStatus ?? 0;

  if (
    code === 'SNAP_APP_SESSION_EXPIRED' ||
    /application session has expired/i.test(raw) ||
    /kitchenpro session has expired/i.test(raw)
  ) {
    return createSnapError(
      'bootstrap_expired',
      SnapDenyCodes.BootstrapExpired,
      CustomerMessages.applicationSessionExpired,
      { correlationId: input.correlationId || undefined, retryable: true, userSafe: true }
    );
  }

  if (code === 'SNAP_APP_SESSION_REQUIRED') {
    return createSnapError(
      'authorization_missing',
      SnapDenyCodes.Unauthorized,
      CustomerMessages.applicationSignInRequired,
      { correlationId: input.correlationId || undefined, userSafe: true }
    );
  }

  const looksLikePanelLogin =
    /^please log in\.?$/i.test(raw) ||
    /session expired\.?\s*please log in again/i.test(raw) ||
    /please log in/i.test(raw);

  if (looksLikePanelLogin || code === SnapDenyCodes.Unauthorized || status === 401) {
    if (/expired/i.test(code) || /expired/i.test(raw)) {
      return createSnapError(
        'bootstrap_expired',
        SnapDenyCodes.BootstrapExpired,
        CustomerMessages.sessionExpired,
        { correlationId: input.correlationId || undefined, retryable: true, userSafe: true }
      );
    }
    return createSnapError(
      'authorization_missing',
      SnapDenyCodes.Unauthorized,
      CustomerMessages.applicationSignInRequired,
      { correlationId: input.correlationId || undefined, userSafe: true }
    );
  }

  if (
    code === SnapDenyCodes.EntitlementUnavailable ||
    /not included in this plan/i.test(raw)
  ) {
    return createSnapError(
      'soft_disabled',
      SnapDenyCodes.EntitlementUnavailable,
      CustomerMessages.snapNotInPlan,
      { correlationId: input.correlationId || undefined, userSafe: true }
    );
  }

  if (
    code === SnapDenyCodes.IntegrationDisabled ||
    code === 'SNAP_SDK_DISABLED' ||
    status === 404
  ) {
    return createSnapError(
      'soft_disabled',
      code || SnapDenyCodes.IntegrationDisabled,
      CustomerMessages.snapNotEnabled,
      { correlationId: input.correlationId || undefined, userSafe: true }
    );
  }

  if (code === SnapDenyCodes.GrainMissing) {
    return createSnapError(
      'capability_denied',
      SnapDenyCodes.GrainMissing,
      CustomerMessages.userNotAuthorized,
      { correlationId: input.correlationId || undefined, userSafe: true }
    );
  }

  if (code === SnapDenyCodes.BootstrapExpired || /expired/i.test(code)) {
    return createSnapError(
      'bootstrap_expired',
      SnapDenyCodes.BootstrapExpired,
      CustomerMessages.sessionExpired,
      { correlationId: input.correlationId || undefined, retryable: true, userSafe: true }
    );
  }

  if (code === SnapDenyCodes.AttachmentRejected || code === 'ATTACHMENT_REJECTED') {
    return createSnapError(
      'attachment_rejected',
      SnapDenyCodes.AttachmentRejected,
      CustomerMessages.attachmentRejected,
      { correlationId: input.correlationId || undefined, userSafe: true }
    );
  }

  if (code === SnapDenyCodes.CaptureDisabled || code === SnapDenyCodes.CaptureFailed) {
    return createSnapError(
      code === SnapDenyCodes.CaptureDisabled ? 'capture_disabled' : 'capture_failed',
      code,
      CustomerMessages.captureUnavailable,
      { correlationId: input.correlationId || undefined, userSafe: true }
    );
  }

  if (status >= 500 || code === SnapDenyCodes.TransportFailed) {
    const ref = input.correlationId
      ? ` (reference ${input.correlationId.slice(0, 12)})`
      : '';
    return createSnapError(
      'transport_failed',
      code || SnapDenyCodes.TransportFailed,
      `${CustomerMessages.temporarilyUnavailable}${ref}`,
      { correlationId: input.correlationId || undefined, retryable: true, userSafe: true }
    );
  }

  return createSnapError(
    'submission_denied',
    code || SnapDenyCodes.ConfigurationInvalid,
    CustomerMessages.submissionFailed,
    { correlationId: input.correlationId || undefined, userSafe: true }
  );
}
